import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal, Form } from 'antd';
import { 
    useGetAllOvrs, 
    useGetStaff, 
    useCreateOvr, 
    useRespondToOvr,
    useCloseOvr,
    useMoveOvr,
    useGetOvr
} from '../../../hooks/useOVR';
import { useGetUserData } from '../../../hooks/useGetUserData';

export const useOvrChat = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [selectedOvr, setSelectedOvr] = useState(null);
    const [messageInput, setMessageInput] = useState('');
    const [createForm] = Form.useForm();

    // Get OVR ID from URL parameters
    const ovrIdFromUrl = searchParams.get('ovrId');

    // Get current user data
    const { data: userData } = useGetUserData();
    const currentUserId = userData?.id;
    const role = localStorage.getItem("role");

    // API Queries
    const { data: ovrsData, isLoading: ovrsLoading, refetch: refetchOvrs } = useGetAllOvrs({ 
        pageNumber: 1, 
        pageSize: 100 
    });
    
    const { data: staffData, isLoading: staffLoading } = useGetStaff({ 
        pageSize: 100 
    });

    const { data: specificOvrData, isLoading: specificOvrLoading } = useGetOvr(ovrIdFromUrl);

    const createOvrMutation = useCreateOvr();
    const respondToOvrMutation = useRespondToOvr();
    const closeOvrMutation = useCloseOvr();
    const moveOvrMutation = useMoveOvr();

    const ovrs = ovrsData?.items || [];

    // Handle opening specific OVR from URL parameter
    useEffect(() => {
        if (ovrIdFromUrl && specificOvrData) {
            setSelectedOvr(specificOvrData);
            // Clear the URL parameter after opening
            searchParams.delete('ovrId');
            setSearchParams(searchParams, { replace: true });
        }
    }, [ovrIdFromUrl, specificOvrData, searchParams, setSearchParams]);

    // Update selected OVR when data changes
    useEffect(() => {
        if (selectedOvr && ovrs.length > 0) {
            const updatedOvr = ovrs.find(o => o.id === selectedOvr.id);
            if (updatedOvr) {
                setSelectedOvr(updatedOvr);
            }
        }
    }, [ovrs, selectedOvr]);

    // Updated to accept the complete payload from the modal
    const handleCreateOvr = async (payload) => {
        try {
            // The payload is already built by the modal with all necessary fields
            await createOvrMutation.mutateAsync(payload);

            // Refetch to get the new OVR
            await refetchOvrs();
            
            setIsCreateModalVisible(false);
            createForm.resetFields();
            
            // Auto-select the newly created OVR
            const newOvrs = await refetchOvrs();
            const newOvr = newOvrs.data?.items?.[0];
            if (newOvr) {
                setSelectedOvr(newOvr);
            }
        } catch (err) {
            console.error('Create OVR error:', err);
        }
    };

    const handleSendMessage = async (message, photoId = null, docId = null) => {
        // Allow sending if there's a message, photo, or doc
        if (!message?.trim() && !photoId && !docId) return;
        if (!selectedOvr || !currentUserId) return;
        
        try {
            await respondToOvrMutation.mutateAsync({
                ovrId: selectedOvr.id,
                userId: currentUserId,
                comment: message || '', // Send empty string if no message but has attachments
                photoId: photoId,
                docId: docId
            });

            setMessageInput('');
            
            // Refetch to get updated comments
            await refetchOvrs();
        } catch (err) {
            console.error('Send message error:', err);
        }
    };

    const handleCloseOvr = (ovrId) => {
        Modal.confirm({
            title: 'Close this conversation?',
            content: 'This will mark the OVR as closed.',
            okText: 'Yes, Close',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await closeOvrMutation.mutateAsync(ovrId);
                    await refetchOvrs();
                } catch (err) {
                    console.error('Close OVR error:', err);
                }
            }
        });
    };

    const handleMoveOvr = async (ovrId, newAdminUserId, newSecretaryUserId) => {
        try {
            await moveOvrMutation.mutateAsync({
                ovrId: ovrId,
                newAdminUserId: newAdminUserId,
                newSecretaryUserId: newSecretaryUserId
            });
            await refetchOvrs();
        } catch (err) {
            console.error('Move OVR error:', err);
        }
    };

    const getStatusBadge = (status) => {
        const config = {
            0: { color: 'green', text: 'Opened' },
            1: { color: '#faad14', text: 'In Progress' },
            2: { color: 'red', text: 'Closed' },
        };
        return config[status] || config[0];
    };

    const getStaffInfo = (userId) => {
        return staffData?.staff?.find(s => s.userId === userId);
    };

    const isMessageFromCurrentUser = (userId) => {
        return userId === currentUserId;
    };

    return {
        // State
        isCreateModalVisible,
        setIsCreateModalVisible,
        selectedOvr,
        setSelectedOvr,
        messageInput,
        setMessageInput,
        createForm,
        role,
        currentUserId,
        
        // Data
        ovrs,
        staffData,
        
        // Loading states
        ovrsLoading,
        staffLoading,
        specificOvrLoading,
        isCreating: createOvrMutation.isLoading,
        isSending: respondToOvrMutation.isLoading,
        isClosing: closeOvrMutation.isLoading,
        isMoving: moveOvrMutation.isLoading,
        
        // Handlers
        handleCreateOvr,
        handleSendMessage,
        handleCloseOvr,
        handleMoveOvr,
        
        // Helpers
        getStatusBadge,
        getStaffInfo,
        isMessageFromCurrentUser,
    };
};